import Client, { ClientDTO, ClientRemoveResponse } from '../../../models/Client';
import {FpAxios} from '@/services/FpAxios';
import { HistoryElement, HistoryElementDTO } from '@/models/HistoryElement';
import { ClientFilter, FiltersResponse } from '@/models/Filter';
import { BikReport, BikReportSummarySimple } from '@/models/BikReport';
import { BikReportOld } from '@/models/BikReportOld';
import { clone as _clone } from 'lodash-es';
import Note, { NoteDTO } from '@/models/Note';
import { AnonymousBorrower } from '@/modules/client/models/AnonymousBorrower';
import { clone, Utils } from '@/services/utils/BasicUtils';
import { mapForValidation } from '@/modules/multiForm/shared/MultiformModels';
import { ChangeOwnershipData, OwnershipChangeStatus } from '@/modules/client/models/OwnershipStatus';
import { SimpleDelegation } from '@/components/prospects/simpleDelegation/SimpleDelegation';
import {
  UpdateClientAgreements
} from '@/modules/client/views/client-agreements/Agreements';
import processProxy from '@/env/processProxy';
import { ClientPlatformStatus } from '@/models/Deal';

class ClientApi {
  private clientAxios = FpAxios.create();
  private baseUrl = 'clients';
  private bigApiUrl = processProxy.VUE_APP_BIG_API_URL!;

  async clientsSearch(query?: Record<string, string | boolean>, omitError: boolean = false): Promise<Client | undefined> {
    const response = await this.clientAxios.post<ClientDTO>(`${this.baseUrl}/find`, query);
    if (response.data) {
      return new Client(response.data);
    } else if (!omitError) {
      throw new Error('Can\'t find client');
    }
    return undefined;
  }

  async getClient(id: number): Promise<Client> {
    const response = await this.clientAxios.get<Client>(`${this.baseUrl}/${id}`);
    return new Client(response.data);
  }

  async getClients(): Promise<Array<Client>> {
    const response = await this.clientAxios.get<Array<ClientDTO>>('/clients');
    return response.data.map((client: ClientDTO) => new Client(client));
  }

  async addClient(client: Client): Promise<Client> {
    const clientToSend = client.isCompany ? client.companyObject : client.clientObject;
    const response = await this.clientAxios.post<ClientDTO>(`${this.baseUrl}/with-sms`, clientToSend);
    return new Client(response.data);
  }

  async addClientByScans(clientFormData: FormData): Promise<Client> {
    const response = await this.clientAxios.post(`${this.baseUrl}/with-scans`, clientFormData);
    return response.data;
  }

  async addClientByPhone(client: Client): Promise<Client> {
    const response = await this.clientAxios.post(`${this.baseUrl}/with-phone`, client);
    return new Client(response.data);
  }

  async editClientByUser(client: Partial<Client>): Promise<Client> {
    const data: Partial<Client> = clone(client);
    if (data.displayName) {
      data.name = data.displayName;
    }
    const clientToSend = data.isCompany ? data.companyObject : data.clientObject;
    if (clientToSend) {
      const response = await this.clientAxios.put(`${this.baseUrl}/${client.id}`, clientToSend);
      return new Client(response.data);
    } else {
      throw new Error('No clientToSand found');
    }
  }

  async editClient(client: Partial<ClientDTO>): Promise<Client> {
    const response = await this.clientAxios.put(`${this.baseUrl}/${client.id}`, client);
    return new Client(response.data);
  }

  async editClientAgreements(clientId: number, agreements: UpdateClientAgreements): Promise<Client> {
    const response = await this.clientAxios.put<ClientDTO>(`${this.baseUrl}/${clientId}/agreements`, agreements);
    return new Client(response.data);
  }

  async editClientDataJSON(client: Client): Promise<Client> {
    client.clientDataJson = mapForValidation(client.clientDataJson!);
    const data = Utils.changeToNull(_clone(client));
    const response = await this.clientAxios.put<ClientDTO>(`${this.baseUrl}/${client.id}/data`, data);
    return new Client(response.data);
  }

  async getClientHistory(id: number): Promise<Array<HistoryElement<any>>> {
    const response = await this.clientAxios.get<Array<HistoryElement<any>>>(`${this.baseUrl}/${id}/history`);
    return response.data.map((history: HistoryElementDTO<Client>) => new HistoryElement(history));
  }

  async filterClients(filters?: ClientFilter): Promise<FiltersResponse<Client>> {
    const response = await this.clientAxios
      .post<FiltersResponse<Client>>(`${this.baseUrl}/search`, filters ? filters.apiData : {});
    response.data.items = response.data.items.map((dto: ClientDTO) => new Client(dto));
    return response.data;
  }

  async addNote(clientId: number, data: NoteDTO): Promise<Note> {
    const response = await this.clientAxios.post<NoteDTO>(`${this.baseUrl}/${clientId}/notes`, data);
    return new Note(response.data);
  }

  async sendBigRequestSms(clientId: number): Promise<BikReport> {
    const response = await this.clientAxios.post(`/big/inquiry/for-client/${clientId}`);
    return new BikReport(response.data);
  }

  async removeClients(clientsIds: Array<number>): Promise<ClientRemoveResponse> {
    const response = await this.clientAxios.delete(this.baseUrl, { data: clientsIds, });
    return response.data;
  }

  async getBikReports(clientId: number): Promise<Array<BikReport>> {
    // New BIG API endpoint (prefixed by VUE_APP_BIG_API_URL): list/client/{clientId}
    const response = await this.clientAxios.get<Array<BikReport>>(`${this.bigApiUrl}list/client/${clientId}`);
    return response.data.map(x => new BikReport(x));
  }

  async getBikReportsSimple(clientId: number): Promise<Array<BikReportSummarySimple>> {
    const response = await this.clientAxios.get<Array<BikReportSummarySimple>>(`${this.bigApiUrl}list/client/${clientId}/basic`);
    return response.data;
  }

  async getBikReportsOld(clientId: number): Promise<Array<BikReportOld>> {
    const response = await this.clientAxios.get<Array<BikReportOld>>(`/big/${clientId}`);
    return response.data.map(x => new BikReportOld(x));
  }

  async removeClient(id: number) {
    await this.clientAxios.delete(`${this.baseUrl}/${id}`);
  }

  async sendCreationEmails(clientId: number) {
    const response = await this.clientAxios.post(`${this.baseUrl}/${clientId}/send-creation-emails`);
    return response.data;
  }

  async changeEditableByClientFlag(clientId: number, editable: boolean): Promise<void> {
    await this.clientAxios.put(`${this.baseUrl}/${clientId}/is-editable-by-client`, editable);
  }

  async createClientFromAnonymousWithSMS(borrower: AnonymousBorrower, client: Client): Promise<Client> {
    const response = await this.clientAxios.post<ClientDTO>(`deals/${borrower.dealId}/anonymous-borrowers/${borrower.id}/with-sms`, client);
    return new Client(response.data);
  }

  async createClientFromAnonymousWithScans(borrower: AnonymousBorrower, clientFormData: FormData): Promise<Client> {
    const response = await this.clientAxios.post<ClientDTO>(`deals/${borrower.dealId}/anonymous-borrowers/${borrower.id}/with-scans`, clientFormData);
    return new Client(response.data);
  }

  async mergeBorrowerWithClient(borrower: AnonymousBorrower, clientId: number): Promise<Client> {
    const response = await this.clientAxios.put<ClientDTO>(`deals/${borrower.dealId}/anonymous-borrowers/${borrower.id}/client/${clientId}`);
    return new Client(response.data);
  }

  async getOwnershipStatus(clientId: number): Promise<OwnershipChangeStatus> {
    const response = await this.clientAxios.get<OwnershipChangeStatus>(`clients/${clientId}/change-ownership`);
    return response.data;
  }

  async changeOwnership(clientId: number, data: ChangeOwnershipData): Promise<OwnershipChangeStatus> {
    const response = await this.clientAxios.post<OwnershipChangeStatus>(`clients/${clientId}/change-ownership`, data);
    return response.data;
  }

  

  async getClientBankSecrecyDetails(clientId: number): Promise<any> {
    const response = await this.clientAxios.get(`clients/bank-secrecy/${clientId}`);
    return response.data;
  }

  async sendClientBankSecrecyAgreement(clientId: number, partnerId: number): Promise<any> {
    const response = await this.clientAxios.put(`clients/bank-secrecy/${clientId}/${partnerId}/send`);
    return response.data;
  }

  async acceptBankSecrecy(token: string): Promise<any> {
    const response = await this.clientAxios.put(`clients/bank-secrecy/${token}/accept`);
    return response;
  }

  async rejectBankSecrecy(token: string): Promise<any> {
    const response = await this.clientAxios.put(`clients/bank-secrecy/${token}/reject`);
    return response;
  }

  async getClientBankSecrecyByToken(token: string): Promise<any> {
    const response = await this.clientAxios.get(`clients/bank-secrecy/token/${token}`);
    return response.data;
  }

  async simpleDelegation(data: SimpleDelegation): Promise<SimpleDelegation> {
    const response = await this.clientAxios.post<SimpleDelegation>(`clients/${data.clientId}/simple-delegate`, data);
    if (response.data.isSuccess) {
      return response.data;
    } else {
      throw new Error('Nie udało się przekazać danych do Lendi Care');
    }
  }

  async getClientSimpleDelegations(clientId: number): Promise<SimpleDelegation[]> {
    const response = await this.clientAxios.get<SimpleDelegation[]>(`clients/${clientId}/simple-delegate`);
    return response.data;
  }

  async getNotes(clientId: number): Promise<Note[]> {
    const response = await this.clientAxios.get<Note[]>(`clients/${clientId}/notes`);
    return response.data;
  }

  async getClientPlatformStatus(clientId: number): Promise<ClientPlatformStatus> {
    const response = await this.clientAxios.post<ClientPlatformStatus>(`clients/platform-status`,
      {
        clientId,
      }
    );
    return response.data;
  }
}

export default new ClientApi();
